<template>
	<v-dialog v-model="value" max-width="65%" scrollable @click:outside="$emit('input', false)">
		<v-card>
			<v-card-title class="title primary--text mb-2 secondary" v-translate>Recommendations</v-card-title>
			<v-progress-linear
				:active="loadingRecommendations"
				:indeterminate="loadingRecommendations"
				top
				color="primary"
			></v-progress-linear>
			<v-card-text v-if="!loadingRecommendations">
				<template v-if="recommended_formulas.length == 0">
					<p v-if="type === 'formula'" class="grey--text mb-3" v-translate>No Formulas to recommend.</p>
					<p v-else class="grey--text mb-3" v-translate>No Supplements to recommend.</p>
				</template>
				<template v-else>
					<v-progress-linear v-model="coverage" height="25" reactive class="white--text" rounded>
						<strong>{{ Math.ceil(coverage) }}%</strong>
					</v-progress-linear>
					<template v-if="selectedFormulas.length == 0">
						<p v-if="type === 'formula'" class="grey--text my-3" v-translate>No Formulas selected.</p>
						<p v-else class="grey--text my-3" v-translate>No Supplements selected.</p>
					</template>
					<v-list v-else flat>
						<v-list-item-group>
							<v-list-item
								v-for="formula in selectedFormulas"
								:key="formula.id"
								style="border: 1px solid #e0e0e0; border-radius: 5px"
								@click="selectRecomendation(formula)"
							>
								<v-list-item-icon class="mr-3">
									<v-icon v-if="type === 'formula'" color="primary" key="formula">$vuetify.icons.tcmIcon</v-icon>
									<v-icon v-else color="primary" key="supplement">mdi-pill</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>
										<strong>{{formula.name}}</strong>
									</v-list-item-title>
									<v-list-item-subtitle>
										<template v-if="formula.external_entity">
											{{formula.external_entity.name}}
										</template>
										<template v-else>
											<translate>Classic</translate>
										</template>
									</v-list-item-subtitle>
								</v-list-item-content>
								<v-list-item-action>
									<v-row no-gutters align="center">
										<v-checkbox
											@click="selectRecomendation(formula)"
											v-model="formula.selected"
											color="primary"
										></v-checkbox>
									</v-row>
								</v-list-item-action>
							</v-list-item>
						</v-list-item-group>
					</v-list>
					<v-divider></v-divider>
					<template v-if="formulasToRecommend.length == 0">
						<p
							v-if="type === 'formula'"
							class="grey--text my-3"
							v-translate
						>No more Formulas to recommend.</p>
						<p v-else class="grey--text my-3" v-translate>No more Supplements to recommend.</p>
					</template>
					<v-list v-else flat>
						<v-list-item-group>
							<v-list-item
								v-for="formula in formulasToRecommend"
								:key="formula.id"
								style="border: 1px solid #e0e0e0; border-radius: 5px"
								@click="type === 'formula' ? checkFormula(formula) : selectRecomendation(formula)"
							>
								<v-list-item-icon class="mr-3">
									<v-icon v-if="type === 'formula'" color="primary">$vuetify.icons.tcmIcon</v-icon>
									<v-icon v-else color="primary">mdi-pill</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>
										<strong>{{formula.name}}</strong>
									</v-list-item-title>
									<v-list-item-subtitle>
										<template v-if="formula.external_entity">
											{{formula.external_entity.name}}
										</template>
										<template v-else>
											<translate>Classic</translate>
										</template>
									</v-list-item-subtitle>
								</v-list-item-content>
								<v-list-item-action>
									<v-row no-gutters align="center">
										<span class="primary--text font-weight-bold mr-3">+{{formula.percentage}}%</span>
										<v-checkbox
											@click="type === 'formula' ? checkFormula(formula) : selectRecomendation(formula)"
											v-model="formula.selected"
											color="primary"
										></v-checkbox>
									</v-row>
								</v-list-item-action>
							</v-list-item>
						</v-list-item-group>
					</v-list>
					<v-dialog v-model="classicFormulaSubstancesDialog" max-width="40%" scrollable>
						<v-card>
							<v-card-title class="title primary--text mb-2 secondary" v-translate>Substance Quantities (g)</v-card-title>
							<v-card-text>
								<v-form ref="substancesForm" v-model="substancesFormValid">
									<v-row no-gutters class="mt-3">
										<v-col v-for="ing in classicFormulaToAdd.ingredients" cols="12" sm="6" :key="ing.id">
											<v-text-field
												:rules="substanceQuantityRules"
												v-model="ing.quantity"
												:label="ing.name"
												class="px-2"
												outlined
												type="number"
											/>
										</v-col>
									</v-row>
								</v-form>
							</v-card-text>
							<v-divider></v-divider>
							<v-card-actions>
								<v-spacer></v-spacer>
								<v-btn color="primary darken-1" text @click="addClassicFormula">
									<translate>Add</translate>
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</template>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn v-if="selectedFormulas.length > 0" color="primary" @click="addFormulas">
					<translate>Add</translate>
				</v-btn>
				<v-btn color="primary darken-1" text @click="hide">
					<translate>Close</translate>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios";

export default {
	name: "CMMRecommendations",
	props: ["value", "type"],
	data: () => ({
		loadingRecommendations: true,
		diagnosis_icd: [],
		diagnosis_icd_selected: [],
		products_indications: [],
		coverage: 0,
		recommended_formulas: [],
		classicFormulaSubstancesDialog: false,
		substancesFormValid: false,
		classicFormulaToAdd: {ingredients: []}
	}),
	computed: {
		selectedFormulas() {
			return this.recommended_formulas.filter(formula => formula.selected);
		},
		formulasToRecommend() {
			return this.recommended_formulas
				.filter(formula => !formula.selected)
				.sort(this.comparePercentage);
		},
		substanceQuantityRules() {
			return [
				v => !!v || this.$gettext("Required")
				//v => (this.isInt(v) || this.isFloat(v)) || "Invalid quantity"
			];
		}
	},
	methods: {
		hide() {
			this.$emit('input', false);
		},
		comparePercentage(formula1, formula2) {
			return formula2.percentage - formula1.percentage;
		},
		selectRecomendation(formula) {
			formula.selected = !formula.selected;
			this.updatePercentages();
		},
		checkFormula(formula) {
			if(formula.classic) {
				this.classicFormulaToAdd = formula;
				this.classicFormulaSubstancesDialog = true;
			} else {
				this.selectRecomendation(formula);
			}
		},
		addClassicFormula() {
			if(this.$refs.substancesForm.validate()){
				this.classicFormulaSubstancesDialog = false;
				this.selectRecomendation(this.classicFormulaToAdd);
			}
		},
		updatePercentages() {
			// formula.selected = !formula.selected;
			// if (formula.selected) {
			//   this.coverage += formula.percentage;
			// }
			// this.recommended_formulas.forEach(function(formula) {
			//   formula.percentage = formula.percentage + 10;
			// });
			this.diagnosis_icd_selected = [];
			this.selectedFormulas.forEach(formula => {
				var icds = this.products_indications[formula.id];
				icds.forEach(icd => {
					if (
						this.diagnosis_icd &&
						!this.diagnosis_icd_selected.includes(icd)
					) {
						this.diagnosis_icd_selected.push(icd);
					}
				});
			});

			var missing_codes = this.diagnosis_icd.filter(
				item => !this.diagnosis_icd_selected.includes(item)
			);
			var num_missing_codes = missing_codes.length;
			var percentage = (num_missing_codes * 100) / this.diagnosis_icd.length;
			percentage = Math.round(percentage);
			this.coverage = 100 - percentage;

			this.formulasToRecommend.forEach(formula => {
				var icds = this.products_indications[formula.id];

				var missing_covered = [];
				icds.forEach(function(icd) {
					if (missing_codes.includes(icd) && !missing_covered.includes(icd)) {
						missing_covered.push(icd);
					}
				});
				if (missing_codes.length > 0) {
					var percentage =
						(missing_covered.length * 100) / missing_codes.length;
					percentage = Math.round(percentage);
					formula.percentage = percentage;
				} else {
					//hide percentage
				}
			});
		},
		addFormulas() {
			this.$emit("add-recommendations", this.selectedFormulas);
			this.hide();
		},
		getRecommendations() {
			this.loadingRecommendations = true;
			const appointmentId = this.$route.params.aid;
			const patientId = this.$route.params.aid;
			let text;
			if (this.type === "formula") {
				text = "recommended_formulas"
			} else {
				text = "recommended_products"
			}
			axios
				.get("/patient/" + patientId + "/appointment/" + appointmentId + "/" + text)
				.then(response => {
					if (response.status === 200) {
						console.log(response.data);
						var formulas = response.data.products;
						formulas.forEach(formula => {
							formula.selected = false;
							formula.percentage = 0;
						});
						this.recommended_formulas = formulas;
						this.diagnosis_icd = response.data.diagnosis;
						this.products_indications = response.data.products_indications;
						this.updatePercentages();
					}
					this.loadingRecommendations = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingRecommendations = false;
				});
		}
	},
	created() {
		this.getRecommendations();
	}
};
</script>